import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AccountType, Currency, type UserResponse } from '@paladin-monorepo/common';
import { usersApi } from '@/lib/api/users';
import { useAuth } from '@/lib/auth';
import { Loading } from '@/components/Loading';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Users } from 'lucide-react';

type RowRates = { ibr: string; ebr: string };

function parseRateInput(raw: string): number | null {
  const t = raw.trim().replace(',', '.');
  if (t === '') {
    return null;
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

export function CenotvorbaSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'CenotvorbaSettingsTab',
  });

  const isSupplier =
    currentUser?.account?.accountType === AccountType.Consultant ||
    currentUser?.account?.accountType === AccountType.SolutionProvider;

  const accountCurrency =
    currentUser?.account?.defaultCurrency ?? Currency.CZK;

  const { data: users, isLoading } = useQuery<UserResponse[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const [rows, setRows] = useState<Record<string, RowRates>>({});

  useEffect(() => {
    if (!users) {
      return;
    }
    setRows(
      Object.fromEntries(
        users.map((u) => [
          u.id,
          {
            ibr: u.internalBusinessRate != null ? String(u.internalBusinessRate) : '',
            ebr: u.externalBusinessRate != null ? String(u.externalBusinessRate) : '',
          },
        ]),
      ),
    );
  }, [users]);

  const saveMutation = useMutation({
    mutationFn: async ({ userId, ibr, ebr }: { userId: string; ibr: string; ebr: string }) => {
      const internalBusinessRate = parseRateInput(ibr);
      const externalBusinessRate = parseRateInput(ebr);
      if (
        Number.isNaN(internalBusinessRate) ||
        Number.isNaN(externalBusinessRate) ||
        (internalBusinessRate !== null && internalBusinessRate < 0) ||
        (externalBusinessRate !== null && externalBusinessRate < 0)
      ) {
        throw new Error('invalid');
      }
      return usersApi.update(userId, { internalBusinessRate, externalBusinessRate });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success(t('settings.cenotvorba.saveSuccess'));
    },
    onError: (err: Error) => {
      if (err.message === 'invalid') {
        toast.error(t('settings.cenotvorba.invalidRates'));
      } else {
        toast.error(t('settings.cenotvorba.saveError'));
      }
    },
  });

  if (!isSupplier) {
    return <Navigate to="/settings/general" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.cenotvorba.title')}</CardTitle>
        <CardDescription>{t('settings.cenotvorba.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!users || users.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('users.list.empty.title')}
            description={t('users.list.empty.description')}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.list.columns.email')}</TableHead>
                <TableHead>{t('users.list.columns.name')}</TableHead>
                <TableHead>{t('users.list.columns.role')}</TableHead>
                <TableHead>{t('settings.cenotvorba.columns.ibr')}</TableHead>
                <TableHead>{t('settings.cenotvorba.columns.ebr')}</TableHead>
                <TableHead className="w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const row = rows[u.id] ?? { ibr: '', ebr: '' };
                const saving = saveMutation.isPending && saveMutation.variables?.userId === u.id;
                return (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t(`codebook.role.${u.role}`)}</Badge>
                    </TableCell>
                    <TableCell>
                      <InputGroup className="max-w-[200px]">
                        <InputGroupInput
                          type="text"
                          inputMode="decimal"
                          value={row.ibr}
                          onChange={(e) =>
                            setRows((prev) => ({
                              ...prev,
                              [u.id]: { ...row, ibr: e.target.value },
                            }))
                          }
                          disabled={saving}
                          aria-label={`${t('settings.cenotvorba.columns.ibr')} (${accountCurrency})`}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText className="tabular-nums">
                            {accountCurrency}
                          </InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </TableCell>
                    <TableCell>
                      <InputGroup className="max-w-[200px]">
                        <InputGroupInput
                          type="text"
                          inputMode="decimal"
                          value={row.ebr}
                          onChange={(e) =>
                            setRows((prev) => ({
                              ...prev,
                              [u.id]: { ...row, ebr: e.target.value },
                            }))
                          }
                          disabled={saving}
                          aria-label={`${t('settings.cenotvorba.columns.ebr')} (${accountCurrency})`}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText className="tabular-nums">
                            {accountCurrency}
                          </InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={saving}
                        onClick={() =>
                          saveMutation.mutate({ userId: u.id, ibr: row.ibr, ebr: row.ebr })
                        }
                      >
                        {saving ? t('settings.cenotvorba.saving') : t('settings.cenotvorba.save')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
